import { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../context/TenantContext';

const ICON_SUGGESTIONS = ['📭', '⚙️', '✓', '✕', '⏸', '🔍', '📦', '🚚', '⏳', '⭐', '🔥', '•'];
const COLOR_SUGGESTIONS = ['#0ea5e9', '#f59e0b', '#10b981', '#6b7280', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

export default function TaskStatusesPanel({ canEdit }) {
  const { tenantFetch } = useTenant();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tenantFetch('/api/task-statuses');
      const data = await res.json();
      if (res.ok) setStatuses(data.statuses || []);
    } finally {
      setLoading(false);
    }
  }, [tenantFetch]);

  useEffect(() => { load(); }, [load]);

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= statuses.length) return;
    const reordered = [...statuses];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setStatuses(reordered); // optimistic
    try {
      const res = await tenantFetch('/api/task-statuses/order', {
        method: 'PUT',
        body: JSON.stringify({ ids: reordered.map(s => s._id) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setStatuses(data.statuses || reordered);
    } catch (err) {
      setError(err.message);
      load();
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Status „${s.label}" wirklich löschen?`)) return;
    setError('');
    try {
      const res = await tenantFetch(`/api/task-statuses/${s._id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const setInitial = async (s) => {
    setError('');
    try {
      const res = await tenantFetch(`/api/task-statuses/${s._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isInitial: true })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleFinal = async (s) => {
    setError('');
    try {
      const res = await tenantFetch(`/api/task-statuses/${s._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isFinal: !s.isFinal })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <p className="section-hint">Lädt…</p>;

  return (
    <div className="task-statuses-panel">
      {error && <div className="error-message small">{error}</div>}

      <ul className="task-statuses-list">
        {statuses.map((s, idx) => (
          <li key={s._id} className="task-status-row">
            <div className="task-status-order">
              <button
                className="icon-btn"
                onClick={() => move(idx, -1)}
                disabled={!canEdit || idx === 0}
                title="nach oben"
              >▲</button>
              <button
                className="icon-btn"
                onClick={() => move(idx, +1)}
                disabled={!canEdit || idx === statuses.length - 1}
                title="nach unten"
              >▼</button>
            </div>
            <span className="task-status-badge" style={{ color: s.color, borderColor: s.color }}>
              {s.icon} {s.label}
            </span>
            <span className="task-status-flags">
              {s.isInitial && <span className="task-status-tag" title="Neuer Tasks starten mit diesem Status">Initial</span>}
              {s.isFinal && <span className="task-status-tag final" title="Tasks gelten damit als abgeschlossen">Final</span>}
            </span>
            {canEdit && (
              <div className="task-status-actions">
                <button className="link-btn" onClick={() => setEditingId(s._id)}>✎ Bearbeiten</button>
                {!s.isInitial && <button className="link-btn" onClick={() => setInitial(s)}>Als Initial</button>}
                <button className="link-btn" onClick={() => toggleFinal(s)}>
                  {s.isFinal ? '↺ Nicht final' : '✓ Final'}
                </button>
                <button className="link-btn danger" onClick={() => remove(s)}>🗑️</button>
              </div>
            )}
            {editingId === s._id && (
              <EditStatusForm
                status={s}
                onClose={() => setEditingId(null)}
                onSaved={() => { setEditingId(null); load(); }}
              />
            )}
          </li>
        ))}
      </ul>

      {canEdit && (
        showNew ? (
          <NewStatusForm onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
        ) : (
          <button className="primary-btn" onClick={() => setShowNew(true)}>+ Neuer Status</button>
        )
      )}
    </div>
  );
}

function StatusForm({ initial = {}, onSubmit, onClose, submitLabel }) {
  const [label, setLabel] = useState(initial.label || '');
  const [icon, setIcon] = useState(initial.icon || '•');
  const [color, setColor] = useState(initial.color || '#6b7280');
  const [isFinal, setIsFinal] = useState(!!initial.isFinal);
  const [isInitial, setIsInitial] = useState(!!initial.isInitial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { setErr('Label erforderlich.'); return; }
    setBusy(true);
    try {
      await onSubmit({ label: label.trim(), icon, color, isFinal, isInitial });
    } catch (er) {
      setErr(er.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="task-status-form" onSubmit={submit}>
      {err && <div className="error-message small">{err}</div>}
      <div className="task-status-form-row">
        <label>
          Label
          <input className="text-input" value={label} onChange={e => setLabel(e.target.value)} autoFocus />
        </label>
        <label>
          Icon
          <input
            className="text-input task-status-icon-input"
            value={icon}
            onChange={e => setIcon(e.target.value.slice(0, 4))}
            list="task-status-icons"
          />
          <datalist id="task-status-icons">
            {ICON_SUGGESTIONS.map(i => <option key={i} value={i} />)}
          </datalist>
        </label>
        <label>
          Farbe
          <input type="color" value={color} onChange={e => setColor(e.target.value)} />
        </label>
      </div>
      <div className="task-status-form-row">
        <div className="task-status-color-suggestions">
          {COLOR_SUGGESTIONS.map(c => (
            <button
              key={c}
              type="button"
              className="task-status-color-chip"
              style={{ background: c, outline: c === color ? '2px solid #fff' : 'none' }}
              onClick={() => setColor(c)}
              title={c}
            />
          ))}
        </div>
      </div>
      <div className="task-status-form-row checkboxes">
        <label>
          <input type="checkbox" checked={isInitial} onChange={e => setIsInitial(e.target.checked)} />
          Initial-Status (neue Tasks starten hier)
        </label>
        <label>
          <input type="checkbox" checked={isFinal} onChange={e => setIsFinal(e.target.checked)} />
          Final (Task gilt als abgeschlossen)
        </label>
      </div>
      <div className="modal-actions">
        <button type="submit" className="btn-sm-primary" disabled={busy}>{submitLabel}</button>
        <button type="button" className="btn-sm-cancel" onClick={onClose}>Abbrechen</button>
      </div>
    </form>
  );
}

function NewStatusForm({ onClose, onCreated }) {
  const { tenantFetch } = useTenant();
  return (
    <StatusForm
      submitLabel="Anlegen"
      onClose={onClose}
      onSubmit={async (body) => {
        const res = await tenantFetch('/api/task-statuses', {
          method: 'POST',
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Fehler');
        onCreated();
      }}
    />
  );
}

function EditStatusForm({ status, onClose, onSaved }) {
  const { tenantFetch } = useTenant();
  return (
    <StatusForm
      initial={status}
      submitLabel="Speichern"
      onClose={onClose}
      onSubmit={async (body) => {
        const res = await tenantFetch(`/api/task-statuses/${status._id}`, {
          method: 'PATCH',
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Fehler');
        onSaved();
      }}
    />
  );
}
